import { get, post, put } from '@/services/http';
import { FieldDefinition, PayloadLimits } from '@/models/flow_config';

/**
 * Flow configuration for a merchant
 */
export interface FlowConfig {
  enabled: boolean;
  triggersEnabled: Record<string, boolean>;
}

/**
 * Flow event record representing a triggered Shopify Flow event
 */
export interface FlowEvent {
  /** Database primary key (internal use only) */
  id: number;
  /** Public event identifier used for API operations (e.g., retry, lookup) */
  eventId: string;
  /** Type of event that was triggered (e.g., 'purchase_order_created') */
  eventType: string;
  /** Merchant this event belongs to */
  merchantId: number;
  /** ID of the resource that triggered the event */
  resourceId: number;
  /** Type of resource (e.g., 'PurchaseOrder') */
  resourceType: string;
  /** Current processing status */
  status: 'pending' | 'sent' | 'failed';
  /** Number of send attempts made */
  attempts: number;
  /** Error message from the most recent failed attempt */
  lastError?: string;
  /** Event payload sent to Shopify Flow */
  payload: Record<string, unknown>;
  /** Response received from Shopify Flow API */
  response?: Record<string, unknown>;
  /** Timestamp when event was successfully processed */
  processedAt?: string;
  /** Timestamp when event record was created */
  createdAt: string;
  /** Timestamp when event record was last updated */
  updatedAt: string;
}

/**
 * Available trigger definition from the backend
 */
export interface AvailableTrigger {
  eventType: string;
  resourceType: string;
  triggerHandle: string;
  description: string;
}

/**
 * Response from GET /api/flow/config
 */
export interface FlowConfigResponse {
  data: FlowConfig;
  availableTriggers: AvailableTrigger[];
}

/**
 * Parameters for querying flow events
 */
export interface FlowEventQueryParams {
  page?: number;
  limit?: number;
  status?: 'pending' | 'sent' | 'failed';
  eventType?: string;
}

/**
 * Response from GET /api/flow/triggers/:triggerHandle/fields
 */
export interface TriggerFieldDefinitionsResponse {
  success: boolean;
  data: {
    typeName: string;
    fields: FieldDefinition[];
    payloadLimits: PayloadLimits;
  };
  error?: string;
}

/**
 * Response from GET /api/flow/events
 */
export interface FlowEventsResponse {
  data: FlowEvent[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Service for managing Shopify Flow integration
 */
const shopifyFlowService = {
  /**
   * Get the current Flow configuration for the merchant
   * @returns Promise with config and available triggers
   */
  getConfig(): Promise<FlowConfigResponse> {
    return get('/flow/config');
  },

  /**
   * Update the Flow configuration
   * @param config - The new configuration
   * @returns Promise with the updated config
   */
  updateConfig(config: FlowConfig): Promise<FlowConfig> {
    return put('/flow/config', config);
  },

  /**
   * Get Flow event history
   * @param params - Query parameters for filtering and pagination
   * @returns Promise with paginated events
   */
  getEvents(params?: FlowEventQueryParams): Promise<FlowEventsResponse> {
    return get('/flow/events', params);
  },

  /**
   * Get a single Flow event by ID
   * @param eventId - The event ID
   * @returns Promise with the event details
   */
  async getEventById(eventId: string): Promise<FlowEvent> {
    const response = await get<{ data: FlowEvent }>(`/flow/events/${eventId}`);
    return response.data;
  },

  /**
   * Retry a failed Flow event
   * @param eventId - The event ID to retry
   * @returns Promise with the retry result
   */
  retryEvent(eventId: string): Promise<{ success: boolean; message: string; error?: string }> {
    return post(`/flow/events/${eventId}/retry`, {});
  },

  /**
   * Get field definitions for a specific trigger
   * @param triggerHandle - The trigger handle (e.g., 'assisty-low-stock-report')
   * @returns Promise with field definitions and payload limits
   */
  getFieldDefinitions(triggerHandle: string): Promise<TriggerFieldDefinitionsResponse> {
    return get(`/flow/triggers/${triggerHandle}/fields`);
  },
};

export default shopifyFlowService;
